import { useNavigate } from "@tanstack/react-router";
import {
  ActivityQuery,
  ControlCode,
  FindingsActivity,
  Platform,
  cn,
  getControlName,
  getStatusColor,
  useFindingsQuery,
  useRecentEndpointQuery,
  useTodayProtectedQuery,
} from "@theprelude/core";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  DropdownContent,
  Dropdown,
  DropdownTrigger,
  IconButton,
  Loading,
  ShieldFile,
  ShieldIcon,
  PlatformIcon,
  DropdownRadio,
  DropdownRadioGroup,
} from "@theprelude/ds";
import { useCallback, useMemo, useState, type JSX } from "react";
import { osMapping } from "@/lib/operating-system";
import { ActivityQuerySchema } from "@/lib/validators";

function formatOption<T>(value: T, label: string) {
  return {
    value,
    label,
  };
}

export type ConfigurationOptions = {
  dos: Platform[];
  control: ControlCode[];
  policy: string[];
  os: string[];
};

export const useConfigurations = (query: ActivityQuery) => {
  const powerQuery = useFindingsQuery({
    start: query.start,
    finish: query.finish,
  });

  const validOptions: ConfigurationOptions = useMemo(() => {
    const data = powerQuery.data ?? [];

    const dos = [
      ...new Set(data.filter((c) => c.dos).map((c) => c.dos)),
    ] as Platform[];
    const control = [
      ...new Set(
        data.filter((c) => c.control !== undefined).map((c) => c.control),
      ),
    ] as ControlCode[];
    const policy = [
      ...new Set(data.filter((c) => c.policy).map((c) => c.policy)),
    ] as string[];
    const os = [
      ...new Set(
        data.filter((c) => c.dos === query.dos && c.os).map((c) => c.os),
      ),
    ] as string[];

    return {
      dos,
      control,
      policy,
      os,
    };
  }, [powerQuery.data, query.dos]);

  return { validOptions, powerQuery };
};

export default function Configurations({
  query,
  activity,
}: {
  query: ActivityQuery;
  activity: ActivityQuerySchema;
}) {
  const navigate = useNavigate({ from: "/activity" });
  const [cardKey, setCardKey] = useState(0);
  const { validOptions, powerQuery } = useConfigurations(query);
  const protection = useTodayProtectedQuery(query);
  const color = protection.data ? getStatusColor(protection.data) : null;
  const { data: policyOptions } = useRecentEndpointQuery((endpoints) => {
    const policyNames: Record<string, string> = {};
    for (const endpoint of endpoints) {
      if (endpoint.policy && endpoint.policy_name) {
        policyNames[endpoint.policy] = endpoint.policy_name;
      }
    }

    return validOptions.policy.map((d) => formatOption(d, policyNames[d] ?? d));
  });

  const handleFilter = useCallback(
    (configuration: FindingsActivity) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          activity: {
            ...prev.activity,
            dos: configuration.dos ?? undefined,
            os: configuration.os ?? undefined,
            control: configuration.control ?? undefined,
            policy: configuration.policy ?? undefined,
          },
        }),
      });
    },
    [navigate],
  );

  const sortedConfigurations = useMemo(
    () =>
      powerQuery.data?.sort((a, b) => {
        const improvementA = a.finding?.improvement || 0;
        const improvementB = b.finding?.improvement || 0;

        if (improvementA < improvementB) {
          return 1;
        } else if (improvementA > improvementB) {
          return -1;
        } else {
          return 0;
        }
      }) ?? [],
    [powerQuery.data],
  );

  const setCard = useCallback(
    (n: number) => {
      setCardKey(n);
      const configuration = sortedConfigurations[n];
      configuration && handleFilter(configuration);
    },
    [sortedConfigurations, handleFilter],
  );

  const handleCardKey = (n: number) => {
    const newKey = cardKey + n;
    if (powerQuery.data && newKey === powerQuery.data?.length) {
      return setCard(0);
    }
    if (powerQuery.data && newKey < 0) {
      return setCard(powerQuery.data.length - 1);
    }
    return setCard(newKey);
  };

  return (
    <div className="flex flex-col items-center gap-4 px-4 lg:flex-row">
      <div className="relative flex w-full items-center lg:w-56">
        <div className="flex h-24 w-full items-center justify-between gap-2 bg-surface-background px-4 py-5 dark:border-2 dark:border-stroke-line dark:bg-secondary-background lg:h-[8.25rem]">
          <IconButton
            className="size-7 shrink-0 rounded-full border border-stroke-line p-1"
            icon={<ArrowLeft />}
            onClick={() => handleCardKey(-1)}
          />
          <div className="text-center">
            {protection.isLoading ? (
              <div className="flex w-full justify-center">
                <Loading className="h-6 w-6" />
              </div>
            ) : (
              <div className="flex flex-row items-center gap-1 lg:flex-col">
                <span className={cn("text-3xl", color)}>
                  {protection.data}%
                </span>
                <span className="w-full text-base lg:w-24 lg:text-xs">
                  Protection with this profile
                </span>
              </div>
            )}
          </div>
          <IconButton
            className="size-7 shrink-0 rounded-full border border-stroke-line p-1"
            icon={<ArrowRight />}
            onClick={() => handleCardKey(1)}
          />
        </div>
        <div className="absolute right-[-10px] h-0 w-0 rotate-90 border-x-[6px] border-b-[8px] border-white border-x-transparent dark:border-stroke-line dark:border-x-transparent" />
      </div>
      <div className="grid w-full grid-cols-2 gap-4 lg:grid-cols-4">
        <ConfigurationCard
          title="Platform"
          icon={
            <PlatformIcon
              platform={query.dos}
              className="h-6 w-6 shrink-0 text-primary-foreground"
            />
          }
          options={validOptions.dos.map((d) =>
            formatOption(d, osMapping[d] ?? ""),
          )}
          value={activity.dos}
          onChangeValue={(value) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                activity: {
                  ...prev.activity,
                  dos: value as Platform,
                  os: undefined,
                },
              }),
            });
          }}
        />
        <ConfigurationCard
          title="Operating System"
          icon={
            <PlatformIcon
              platform={query.dos}
              className="h-6 w-6 shrink-0 text-primary-foreground"
            />
          }
          options={validOptions.os.map((d) => formatOption(d, d))}
          value={activity.os}
          onChangeValue={(value) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                activity: {
                  ...prev.activity,
                  os: value,
                },
              }),
            });
          }}
        />
        <ConfigurationCard
          title="Endpoint Defense"
          icon={
            <ShieldIcon className="h-6 w-6 shrink-0 text-primary-foreground" />
          }
          options={validOptions.control
            .sort()
            .map((d) => formatOption(d.toString(), getControlName(d)))}
          value={activity.control?.toString() ?? ""}
          onChangeValue={(value) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                activity: {
                  ...prev.activity,
                  control: value
                    ? (Number.parseInt(value, 10) as ControlCode)
                    : undefined,
                },
              }),
            });
          }}
        />
        <ConfigurationCard
          title="Prevention Policy"
          icon={
            <ShieldFile className="h-6 w-6 shrink-0 text-primary-foreground" />
          }
          options={policyOptions ?? []}
          value={activity.policy}
          onChangeValue={(value) => {
            void navigate({
              search: (prev) => ({
                ...prev,
                activity: {
                  ...prev.activity,
                  policy: value,
                },
              }),
            });
          }}
        />
      </div>
    </div>
  );
}

type ConfigurationCardProps = {
  title: string;
  icon: JSX.Element;
  options: { value: string; label: string }[];
  value?: string;
  onChangeValue: (value?: string) => void;
};

function ConfigurationCard({
  title,
  icon,
  options,
  value,
  onChangeValue,
}: ConfigurationCardProps) {
  const currentValue = options.find((o) => o.value === value)?.label ?? "All";

  return (
    <div className="flex h-24 w-full flex-col rounded-sm border border-stroke-line bg-surface-background p-3 lg:h-[8.25rem]">
      <header className="flex border-b border-stroke-line pb-1 lg:pb-2">
        <span className="flex-grow text-base leading-6 font-medium">
          {title}
        </span>
      </header>
      <main className="mt-[0.65625rem] flex h-[3.9375rem] items-center overflow-hidden">
        <Dropdown>
          <DropdownTrigger asChild>
            <div
              role="button"
              className="flex cursor-pointer items-center gap-2 truncate p-0 text-left text-lg font-normal"
            >
              {icon}
              <span className="truncate">{currentValue}</span>
              <ArrowDown className="shrink-0" />
            </div>
          </DropdownTrigger>
          <DropdownContent align="start" className="min-w-56">
            <DropdownRadioGroup
              defaultValue=""
              value={value}
              onValueChange={onChangeValue}
            >
              <DropdownRadio value="">Any</DropdownRadio>
              {options.map((o) => (
                <DropdownRadio key={o.label} value={o.value}>
                  {o.label}
                </DropdownRadio>
              ))}
            </DropdownRadioGroup>
          </DropdownContent>
        </Dropdown>
      </main>
    </div>
  );
}
